// Что показать по набранному: отбор экранов для палитры.
//
// Отдельным файлом, а не внутри разметки, ровно затем, чтобы его можно было проверить без
// браузера. Отбор — это то место, где палитра бывает бесполезной молча: она честно рисует
// список, просто не тот, и заметить это можно только глазами на кадре.
//
// ## Почему не мера похожести из библиотеки
//
// Она ищет буквы запроса по порядку, но не подряд, — и на коротком русском запросе это даёт
// мусор. Замеры 04.09.2026: «вед» находило «Настройки» (0,03), «Журнал действий» (0,03) и
// «Долги» (0,15), потому что буквы в-е-д складываются из «сер-в-ер», «адр-е-с», «не-д-остача».
// Пять строк в списке, верная одна.
//
// Отсечь это порогом нельзя: верное «люди» → «Реестр людей» весит ровно 0,15, столько же,
// сколько мусорное «вед» → «Долги». Одним числом эти два случая не разделяются никогда.
//
// ## Что вместо неё
//
// Совпадение по НАЧАЛУ СЛОВА. Человек, ищущий словами, печатает начало слова, а не его
// середину: «вед», «зарпл», «чёрн». Правило простое, его видно на глаз, и оно ровно то,
// к которому все привыкли в мессенджерах.
//
// Запрос из нескольких слов ищется целиком: каждое слово запроса обязано найтись началом
// какого-то слова — иначе «чёрный список» не нашёл бы «Чёрный список».
//
// «ё» и «е» считаются одной буквой: «черный» набирают чаще, чем «чёрный», и человек не
// обязан помнить, как записано у нас.

import type { Адрес } from './адреса';
import { сРаскладки } from './раскладка';

/** Вес попадания: в название экрана — вдвое дороже, чем в слово-синоним. */
const ВЕС_НАЗВАНИЯ = 2;
const ВЕС_СИНОНИМА = 1;

function привести(строка: string): string {
  return строка.toLowerCase().replace(/ё/g, 'е');
}

function слова(строка: string): string[] {
  return привести(строка)
    .split(/[^0-9a-zа-я]+/u)
    .filter((слово) => слово !== '');
}

/** Сколько весит попадание одного слова запроса; ноль — не нашлось. */
function весСлова(слово: string, название: readonly string[], синонимы: readonly string[]): number {
  if (название.some((своё) => своё.startsWith(слово))) {
    return ВЕС_НАЗВАНИЯ;
  }
  return синонимы.some((своё) => своё.startsWith(слово)) ? ВЕС_СИНОНИМА : 0;
}

function счётАдреса(адрес: Адрес, искомые: readonly string[]): number {
  const название = слова(адрес.название);
  const синонимы = адрес.синонимы.flatMap(слова);
  let сумма = 0;
  for (const слово of искомые) {
    const вес = весСлова(слово, название, синонимы);
    if (вес === 0) {
      // Хоть одно слово запроса не нашлось — экран не подходит целиком. Иначе «чёрный
      // отпуск» показал бы «Чёрный список» по первому слову и промолчал про второе.
      return 0;
    }
    сумма += вес;
  }
  return сумма;
}

export interface Найденное {
  readonly адрес: Адрес;
  readonly счёт: number;
}

/**
 * Отобрать и упорядочить экраны.
 *
 * Пустой запрос — весь список, сверху раздел, в котором человек стоит: он открыл палитру,
 * работая в «Расчёте», и почти наверняка идёт к соседнему экрану расчёта, а не в «Настройки».
 */
export function отобрать(
  доступные: readonly Адрес[],
  запрос: string,
  разделСейчас: string,
): Найденное[] {
  const очищенный = запрос.trim();
  if (очищенный === '') {
    return [...доступные]
      .map((адрес) => ({ адрес, счёт: 1 }))
      .sort(
        (а, б) =>
          Number(а.адрес.раздел !== разделСейчас) - Number(б.адрес.раздел !== разделСейчас),
      );
  }

  const попытки = [слова(очищенный)];
  const переложено = сРаскладки(очищенный);
  if (переложено !== очищенный) {
    попытки.push(слова(переложено));
  }

  for (const искомые of попытки) {
    if (искомые.length === 0) {
      continue;
    }
    const найдено = доступные
      .map((адрес) => ({ адрес, счёт: счётАдреса(адрес, искомые) }))
      .filter((строка) => строка.счёт > 0)
      // Порядок при равном счёте — исходный: он идёт по разделам, и список не прыгает
      // между наборами букв.
      .sort((а, б) => б.счёт - а.счёт);
    if (найдено.length > 0) {
      return найдено;
    }
  }
  return [];
}
